import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import logger from '../../utils/logger';

/**
 * Top-level error boundary.
 *
 * Catches render-time errors anywhere below it so the user sees a friendly fallback with a
 * Reload action instead of a blank white screen. Logs the error (message only — never the
 * full error/component-stack object, which can contain PII) through the shared logger.
 *
 * Note: this only catches errors thrown during render/lifecycle. Async and event-handler
 * errors are caught by the global window handlers in index.js.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // The last failed API call (if any) stamps its correlation id here so support can tie
    // a UI crash to the server log line. apiClient sets sessionStorage.lastRequestId.
    let requestId;
    try {
      requestId = sessionStorage.getItem('lastRequestId') || undefined;
    } catch (e) {
      requestId = undefined;
    }
    logger.error('React render error', { message: error?.message, requestId });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <Paper elevation={2} sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            An unexpected error occurred. Reloading the page usually fixes it. If the problem
            persists, please contact support.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
